import React from 'react';
import { CheckCircle2, AlertTriangle, AlertOctagon, Info, ShieldCheck, Printer } from 'lucide-react';
import { api } from '../../lib/api';
import { packetView, fmtMoney, MODE_LABEL } from '../../lib/referrals/packet';

const TIER_CLS = {
  green: 'bg-success-soft text-success',
  yellow: 'bg-warning-soft text-warning',
  red: 'bg-danger-soft text-danger',
};

// The funding read for a referral — "PIP covers it", "Strong lien",
// "No payer on file" — computed server-side from the case's coverage.
export function CollectabilityBadge({ referral, size = 'sm' }) {
  const { risk } = packetView(referral);
  if (!risk) return null;
  const Icon = risk.tier === 'green' ? CheckCircle2 : risk.tier === 'red' ? AlertOctagon : AlertTriangle;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-[8px] font-semibold ${TIER_CLS[risk.tier] || TIER_CLS.yellow} ${size === 'lg' ? 'px-3 py-1.5 text-[12px]' : 'px-2.5 py-1 text-[11px]'}`} title={(risk.reasons || []).map((r) => r.text).join(' · ')}>
      <Icon size={size === 'lg' ? 14 : 12} strokeWidth={2.4} />
      {risk.label}
    </span>
  );
}

export function PacketChip({ referral }) {
  const { done, total } = packetView(referral);
  const full = done === total;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-[6px] text-[11px] font-semibold font-mono ${full ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning'}`}>
      {done}/{total}
    </span>
  );
}

// The reasons behind the badge, spelled out.
export function FundingReasons({ referral, className = '' }) {
  const { risk } = packetView(referral);
  if (!risk?.reasons?.length) return null;
  return (
    <ul className={`flex flex-col gap-1 ${className}`}>
      {risk.reasons.map((r, i) => (
        <li key={i} className="flex items-start gap-2 text-[12px]">
          {r.ok
            ? <CheckCircle2 size={13} className="text-success mt-0.5 shrink-0" />
            : <Info size={13} className="text-warning mt-0.5 shrink-0" />}
          <span className={r.ok ? 'text-secondary-c' : 'text-primary-c'}>{r.text}</span>
        </li>
      ))}
    </ul>
  );
}

// "Bill this visit to" — the payer facts the clinic acts on.
export function BillTo({ referral, compact = false, orgId = null }) {
  const { billing } = packetView(referral);
  if (!billing) return null;
  const facts = [];
  if (billing.carrier) facts.push(['Carrier', billing.carrier]);
  if (billing.claim_number) facts.push(['Claim #', billing.claim_number]);
  if (billing.policy_number) facts.push([billing.mode === 'health' ? 'Member ID' : 'Policy #', billing.policy_number]);
  if (billing.adjuster_name) facts.push(['Adjuster', `${billing.adjuster_name}${billing.adjuster_phone ? ` · ${billing.adjuster_phone}` : ''}`]);
  if (billing.limit != null) facts.push(['Limit', `${fmtMoney(billing.limit)}${billing.used != null ? ` · ${fmtMoney(billing.used)} used` : ''}`]);
  if (billing.mode === 'pip') {
    if (billing.named_insured) facts.push(['Named insured', `${billing.named_insured}${billing.insured_relationship ? ` · patient is ${billing.insured_relationship}` : ''}`]);
    facts.push(['EMC', billing.emc_status === 'determined' ? `Determined${billing.emc_provider ? ` — ${billing.emc_provider}` : ''}${billing.emc_date ? ` (${billing.emc_date})` : ''}` : billing.emc_status === 'pending' ? 'Pending' : 'Not on file']);
    if (billing.first_treatment_date) facts.push(['First treatment', billing.first_treatment_date]);
    if (billing.patient_role) facts.push(['Patient was', billing.patient_role]);
  }
  const billTo = ['pip', 'medpay', 'um_uim'].includes(billing.mode) ? [
    ...(billing.billing_address ? [['Claims address', billing.billing_address]] : []),
    ...(billing.billing_fax ? [['Claims fax', billing.billing_fax]] : []),
    ...(billing.payer_id ? [['Payer ID', billing.payer_id]] : []),
    ...(billing.adjuster_email ? [['Adjuster email', billing.adjuster_email]] : []),
  ] : [];
  if (billing.mode === 'lop') {
    if (billing.liability_carrier) facts.push(['At-fault carrier', `${billing.liability_carrier}${billing.liability_claim_number ? ` · ${billing.liability_claim_number}` : ''}`]);
    facts.push(['BI limits', billing.bi_per_person ? `${fmtMoney(billing.bi_per_person)} / ${fmtMoney(billing.bi_per_accident)}` : 'Not on file']);
  }
  return (
    <div className={`rounded-[12px] border border-theme bg-tertiary ${compact ? 'p-3' : 'p-4'}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <div>
          <div className="text-[10.5px] uppercase tracking-wide text-muted-c font-semibold flex items-center gap-1.5"><ShieldCheck size={11} /> Bill this visit to</div>
          <div className={`${compact ? 'text-[16px]' : 'text-[19px]'} font-bold text-primary-c`}>{billing.chip}</div>
          <div className="text-[11px] text-muted-c">{MODE_LABEL[billing.mode] || billing.label}{billing.case_number ? ` · case ${billing.case_number}` : ''}</div>
        </div>
        <CollectabilityBadge referral={referral} size={compact ? 'sm' : 'lg'} />
      </div>
      {billing.note && <div className="text-[11.5px] text-warning mb-2">{billing.note}</div>}
      {facts.length > 0 && (
        <div className={`grid grid-cols-2 ${compact ? 'md:grid-cols-3' : 'md:grid-cols-4'} gap-x-4 gap-y-2 text-[12px] mt-2`}>
          {facts.map(([k, v]) => (
            <div key={k}><div className="text-[10.5px] uppercase font-semibold text-muted-c">{k}</div><div className="text-primary-c font-medium truncate" title={String(v)}>{v}</div></div>
          ))}
        </div>
      )}
      {['pip', 'medpay', 'um_uim'].includes(billing.mode) && (
        <div className="mt-3 pt-3 border-t border-theme">
          <div className="text-[10.5px] uppercase tracking-wide text-muted-c font-semibold mb-1.5">Send bills to</div>
          {billTo.length ? (
            <div className={`grid grid-cols-2 ${compact ? 'md:grid-cols-3' : 'md:grid-cols-4'} gap-x-4 gap-y-2 text-[12px]`}>
              {billTo.map(([k, v]) => (
                <div key={k} className={k === 'Claims address' ? 'col-span-2' : ''}><div className="text-[10.5px] uppercase font-semibold text-muted-c">{k}</div><div className="text-primary-c font-medium">{v}</div></div>
              ))}
            </div>
          ) : (
            <div className="text-[12px] text-warning">Not on file — add the carrier's claims address, fax or payer ID on the case's PIP coverage.</div>
          )}
        </div>
      )}
      {billing.secondary && (
        <div className="mt-2.5 text-[12px] text-secondary-c"><span className="text-[10.5px] uppercase font-semibold text-muted-c mr-2">After that</span>{billing.secondary.label}{billing.secondary.carrier ? ` — ${billing.secondary.carrier}` : ''}{billing.secondary.policy_number ? ` · ${billing.secondary.policy_number}` : ''}</div>
      )}
      {billing.attorney && (billing.attorney.name || billing.attorney.phone || billing.attorney.email) && (
        <div className="mt-2 text-[12px] text-secondary-c"><span className="text-[10.5px] uppercase font-semibold text-muted-c mr-2">Firm contact</span>{[billing.attorney.name, billing.attorney.phone, billing.attorney.email].filter(Boolean).join(' · ')}</div>
      )}
      {(billing.alerts || []).filter((a) => a.level !== 'info').map((a, i) => (
        <div key={i} className={`mt-2 text-[11.5px] font-medium ${a.level === 'critical' ? 'text-danger' : 'text-warning'}`}>{a.text}</div>
      ))}
      {orgId && referral.id && (
        <button onClick={() => api.openReferralCoverSheet(orgId, referral.id).catch((e) => alert(e.message))} className="mt-3 inline-flex items-center gap-1.5 px-3 py-[7px] rounded-[8px] text-[11.5px] font-semibold text-accent-light bg-accent-glow border border-theme hover:opacity-90">
          <Printer size={12} /> Billing cover sheet (PDF)
        </button>
      )}
    </div>
  );
}
